import { useEffect, useState } from 'react'
import { TriangleAlert } from 'lucide-react'
import { Button, Dialog, DialogBody, DialogContent, DialogFooter, Field, Input, Spinner } from '@bb/ui'
import { ApiError } from '../../api/client'
import { de } from '../../i18n'
import { deletionSentence, deletionSummaryInputFromDetail, emailConfirmationMatches } from './deletion'
import { useAdminUser, useDeleteAdminUser } from './useUsers'
import type { DeletionOutcome } from './types'

export interface DeleteAccountDialogProps {
  userId: string | null
  onOpenChange: (open: boolean) => void
  onDeleted: (outcome: DeletionOutcome, person: { firstName: string; lastName: string }) => void
}

/**
 * §6.1 and §7. Fetches the same `AccountDetail` the detail dialog shows —
 * its `deletion.scope` and `usage` counts are what let this say, before the
 * operator commits, exactly which of §6.0's two deletions is about to
 * happen and how much it takes with it. The confirm button stays disabled
 * until the typed text matches the account's own e-mail address
 * (`emailConfirmationMatches`); the server checks again regardless — this
 * gate only decides whether the click is possible, never whether the
 * deletion is allowed.
 */
export function DeleteAccountDialog({ userId, onOpenChange, onDeleted }: DeleteAccountDialogProps) {
  const detail = useAdminUser(userId)
  const remove = useDeleteAdminUser()
  const [typed, setTyped] = useState('')

  // A fresh field every time a different account (or none) is targeted —
  // otherwise a leftover match from the previous account would enable the
  // button for this one before a single keystroke.
  useEffect(() => { setTyped('') }, [userId])

  const person = detail.data?.person
  const matches = person !== undefined && emailConfirmationMatches(typed, person.email)

  function close() {
    if (remove.isPending) return
    onOpenChange(false)
  }

  async function confirm() {
    if (!userId || !person || !matches) return
    // The failure path is already handled — `remove.error` is rendered in
    // the dialog below, and the dialog deliberately stays open so the
    // operator can read it and retry. What was missing is the `catch`: an
    // unawaited rejection from an onClick handler escapes as an unhandled
    // promise rejection in the browser console, which is noise that looks
    // like a defect during exactly the operation an operator is most
    // nervous about. Swallowed here and nowhere else, because this is the
    // one place that can prove the error is already on screen.
    let result
    try {
      result = await remove.mutateAsync({ id: userId, confirmEmail: typed })
    } catch {
      return
    }
    onDeleted(result.deleted, { firstName: person.firstName, lastName: person.lastName })
    onOpenChange(false)
  }

  return (
    <Dialog open={userId !== null} onOpenChange={(open) => !open && close()}>
      <DialogContent width="sm" title={de.users.delete.title}>
        <DialogBody className="space-y-4">
          {detail.isPending || !detail.data ? (
            <div className="flex justify-center py-8"><Spinner className="size-6" /></div>
          ) : (
            <>
              <p className="flex items-start gap-2 text-base text-ink">
                <TriangleAlert aria-hidden className="mt-0.5 size-4 shrink-0 text-danger" />
                <span>
                  {deletionSentence(deletionSummaryInputFromDetail(detail.data))}
                </span>
              </p>

              <Field label={de.users.delete.typeEmailLabel}>
                {({ id }) => (
                  <Input
                    id={id}
                    type="email"
                    autoComplete="off"
                    autoFocus
                    placeholder={de.users.delete.typeEmailPlaceholder}
                    value={typed}
                    onChange={(event) => setTyped(event.target.value)}
                  />
                )}
              </Field>

              {remove.error ? (
                <p role="alert" className="rounded-[var(--radius-control)] bg-danger-soft px-3 py-2 text-base text-danger">
                  {remove.error instanceof ApiError ? remove.error.message : de.common.unknownError}
                </p>
              ) : null}
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" disabled={remove.isPending} onClick={close}>
            {de.common.cancel}
          </Button>
          <Button variant="danger" disabled={!matches} loading={remove.isPending} onClick={confirm}>
            {de.users.delete.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
